import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Lock, Save, Trash2, AlertTriangle, Building2, Receipt, Bell, Plus, MapPin, Palette, ListChecks } from "lucide-react";
import { PRESETS, PRESET_LABELS, EDITABLE, applyTheme, rgbStrToHex, hexToRgbStr, saveLocal, loadLocal } from "../../lib/theme";
import { rank } from "../../lib/rbac";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Settings() {
  const [pw, setPw] = useState({ old_password: "", new_password: "", confirm_password: "" });
  const [pwStatus, setPwStatus] = useState({ type: "", message: "" });
  const [settings, setSettings] = useState(null);
  const [settingsMsg, setSettingsMsg] = useState("");
  const [branches, setBranches] = useState([]);
  const [newBranch, setNewBranch] = useState({ name: "", address: "", phone: "" });
  const [purgeStatus, setPurgeStatus] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const [s, b] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/settings`),
        axios.get(`${BACKEND_URL}/api/branches`),
      ]);
      setSettings(s.data); setBranches(b.data);
    } catch (e) { console.error(e); }
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  const saveSettings = async (e) => {
    e.preventDefault();
    setSettingsMsg("");
    try {
      const { id, ...payload } = settings;
      await axios.put(`${BACKEND_URL}/api/settings`, payload);
      setSettingsMsg("Settings saved!");
      setTimeout(() => setSettingsMsg(""), 2500);
    } catch (err) { setSettingsMsg("Failed to save settings."); }
  };

  const addBranch = async (e) => {
    e.preventDefault();
    if (!newBranch.name) return;
    await axios.post(`${BACKEND_URL}/api/branches`, newBranch);
    setNewBranch({ name: "", address: "", phone: "" });
    fetchData();
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwStatus({ type: "", message: "" });
    if (pw.new_password !== pw.confirm_password) return setPwStatus({ type: "error", message: "Passwords do not match." });
    if (pw.new_password.length < 6) return setPwStatus({ type: "error", message: "Password must be at least 6 characters." });
    try {
      await axios.post(`${BACKEND_URL}/api/auth/change-password`, { old_password: pw.old_password, new_password: pw.new_password });
      setPwStatus({ type: "success", message: "Password updated!" });
      setPw({ old_password: "", new_password: "", confirm_password: "" });
    } catch (err) { setPwStatus({ type: "error", message: err.response?.data?.detail || "Failed." }); }
  };

  const purge = async () => {
    if (window.confirm("FRESH INSTALL: permanently delete ALL members, leads, invoices, packages, staff (except your Admin login), classes, cleaning data and more. Cannot be undone. Continue?")) {
      try { await axios.delete(`${BACKEND_URL}/api/admin/purge-database?full=true`); setPurgeStatus("All data cleared — fresh installation ready."); }
      catch (e) { setPurgeStatus("Failed. Admin only."); }
    }
  };

  const inp = "w-full bg-card_bg border border-border_soft rounded-xl p-3 text-text_primary text-sm focus:ring-1 focus:ring-primary/50";
  const lbl = "text-xs font-semibold text-text_secondary mb-1.5 block uppercase tracking-wider";
  const set = (k, v) => setSettings({ ...settings, [k]: v });

  return (
    <div data-testid="settings-page" className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div>
        <h1 className="text-4xl font-serif text-text_primary mb-2">Settings</h1>
        <p className="text-text_secondary text-sm">Manage gym profile, tax, branches, notifications and your account.</p>
      </div>

      <ThemeAppearance />

      <AgreementEditor />

      <MemberFieldsEditor />

      {settings && (
        <form onSubmit={saveSettings} className="bg-card_bg border border-border_soft rounded-xl p-6 space-y-5">
          <h2 className="text-lg font-bold text-text_primary flex items-center gap-2"><Building2 size={18} className="text-gold" /> Gym Profile</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className={lbl}>Gym Name</label><input data-testid="settings-gym-name" className={inp} value={settings.gym_name || ""} onChange={e => set("gym_name", e.target.value)} /></div>
            <div><label className={lbl}>GSTIN</label><input className={inp} value={settings.gstin || ""} onChange={e => set("gstin", e.target.value)} /></div>
            <div><label className={lbl}>Phone</label><input className={inp} value={settings.phone || ""} onChange={e => set("phone", e.target.value)} /></div>
            <div><label className={lbl}>Email</label><input className={inp} value={settings.email || ""} onChange={e => set("email", e.target.value)} /></div>
            <div className="md:col-span-2"><label className={lbl}>Address</label><input className={inp} value={settings.address || ""} onChange={e => set("address", e.target.value)} /></div>
            <div><label className={lbl}>Business Hours</label><input className={inp} value={settings.business_hours || ""} onChange={e => set("business_hours", e.target.value)} /></div>
            <div><label className={lbl}>Currency</label><input className={inp} value={settings.currency || ""} onChange={e => set("currency", e.target.value)} /></div>
          </div>

          <h2 className="text-lg font-bold text-text_primary flex items-center gap-2 pt-2"><Receipt size={18} className="text-gold" /> Tax & Fees</h2>
          <div className="grid grid-cols-2 gap-4">
            <div><label className={lbl}>Default GST %</label><input type="number" className={inp} value={settings.gst_percent || 0} onChange={e => set("gst_percent", parseFloat(e.target.value))} /></div>
            <div><label className={lbl}>Late Fee (₹)</label><input type="number" className={inp} value={settings.late_fee || 0} onChange={e => set("late_fee", parseFloat(e.target.value))} /></div>
          </div>

          <h2 className="text-lg font-bold text-text_primary flex items-center gap-2 pt-2"><Bell size={18} className="text-gold" /> Notifications</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[["notify_welcome", "Welcome"], ["notify_expiry", "Expiry Reminder"], ["notify_due", "Payment Due"], ["notify_birthday", "Birthday Wish"]].map(([k, label]) => (
              <label key={k} className="flex items-center gap-2 bg-main_bg border border-border_soft rounded-xl px-3 py-2.5 cursor-pointer">
                <input type="checkbox" checked={!!settings[k]} onChange={e => set(k, e.target.checked)} className="accent-gold w-4 h-4" />
                <span className="text-sm text-text_primary">{label}</span>
              </label>
            ))}
          </div>

          {settingsMsg && <p className="text-sm text-accent_success">{settingsMsg}</p>}
          <button data-testid="save-settings-btn" type="submit" className="bg-primary text-[#111827] font-bold py-3 px-6 rounded-xl flex items-center gap-2 hover:bg-primary_hover transition-colors"><Save size={18} /> Save Settings</button>
        </form>
      )}

      {/* Branches */}
      <div className="bg-card_bg border border-border_soft rounded-xl p-6">
        <h2 className="text-lg font-bold text-text_primary flex items-center gap-2 mb-4"><MapPin size={18} className="text-gold" /> Branches</h2>
        <div className="space-y-2 mb-5">
          {branches.map((b) => (
            <div key={b.id} className="flex justify-between items-center bg-main_bg border border-border_soft/60 rounded-xl px-4 py-3">
              <span className="text-sm font-semibold text-text_primary">{b.name}</span>
              <span className="text-xs text-text_secondary">{b.address} {b.phone && `· ${b.phone}`}</span>
            </div>
          ))}
        </div>
        <form onSubmit={addBranch} className="flex flex-col md:flex-row gap-3">
          <input data-testid="branch-name" placeholder="Branch name" value={newBranch.name} onChange={e => setNewBranch({ ...newBranch, name: e.target.value })} className={inp} />
          <input placeholder="Address" value={newBranch.address} onChange={e => setNewBranch({ ...newBranch, address: e.target.value })} className={inp} />
          <input placeholder="Phone" value={newBranch.phone} onChange={e => setNewBranch({ ...newBranch, phone: e.target.value })} className={inp} />
          <button data-testid="add-branch-btn" type="submit" className="bg-primary text-[#111827] px-6 py-3 rounded-xl font-bold text-sm flex items-center gap-2 hover:bg-primary_hover transition-colors whitespace-nowrap"><Plus size={16} /> Add</button>
        </form>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
        <div className="bg-card_bg border border-border_soft p-6 rounded-xl">
          <div className="flex items-center gap-3 mb-6 border-b border-border_soft/30 pb-4">
            <div className="p-3 bg-main_bg rounded-lg text-gold"><Lock size={20} /></div>
            <h2 className="text-xl font-bold text-text_primary">Change Password</h2>
          </div>
          {pwStatus.message && <div className={`mb-4 px-4 py-3 rounded text-sm ${pwStatus.type === "success" ? "bg-accent_success/10 border border-accent_success/30 text-accent_success" : "bg-accent_danger/10 border border-accent_danger/30 text-accent_danger"}`}>{pwStatus.message}</div>}
          <form onSubmit={changePassword} className="space-y-4">
            <div><label className={lbl}>Current Password</label><input required type="password" value={pw.old_password} onChange={e => setPw({ ...pw, old_password: e.target.value })} className={inp} /></div>
            <div><label className={lbl}>New Password</label><input required type="password" value={pw.new_password} onChange={e => setPw({ ...pw, new_password: e.target.value })} className={inp} /></div>
            <div><label className={lbl}>Confirm New Password</label><input required type="password" value={pw.confirm_password} onChange={e => setPw({ ...pw, confirm_password: e.target.value })} className={inp} /></div>
            <button type="submit" className="w-full bg-primary text-[#111827] font-bold py-3 rounded-full mt-2 hover:bg-primary_hover transition-colors flex justify-center items-center gap-2"><Save size={18} /> Update Password</button>
          </form>
        </div>

        <div className="bg-main_bg border border-accent_danger/50 p-6 rounded-xl">
          <div className="flex items-center gap-3 mb-6 border-b border-accent_danger/30 pb-4">
            <div className="p-3 bg-accent_danger/10 rounded-lg text-accent_danger"><AlertTriangle size={20} /></div>
            <h2 className="text-xl font-bold text-accent_danger">Danger Zone</h2>
          </div>
          <p className="text-sm text-text_secondary mb-6">Reset the system to a fresh installation: deletes all members, leads, invoices, packages, staff (except your Admin login) and cleaning data. Your admin account stays intact.</p>
          {purgeStatus && <div className="mb-4 px-4 py-3 rounded text-sm bg-accent_success/10 border border-accent_success/30 text-accent_success font-bold">{purgeStatus}</div>}
          <button onClick={purge} className="w-full bg-accent_danger/20 border border-accent_danger text-accent_danger font-bold py-3 rounded-full hover:bg-accent_danger hover:text-white transition-colors flex justify-center items-center gap-2"><Trash2 size={18} /> Reset to Fresh Install</button>
        </div>
      </div>
    </div>
  );
}

const ThemeAppearance = () => {
  const [vars, setVars] = useState(() => loadLocal() || PRESETS["light-gold"]);
  const setPreset = (p) => { const v = { ...PRESETS[p] }; setVars(v); applyTheme(v); };
  const setColor = (key, hex) => { const v = { ...vars, [key]: hexToRgbStr(hex) }; setVars(v); applyTheme(v); };
  const save = async () => { applyTheme(vars); saveLocal(vars); try { await axios.put(`${BACKEND_URL}/api/user/theme`, { theme: vars }); } catch (e) {} };
  return (
    <div data-testid="appearance-section" className="bg-card_bg border border-border_soft rounded-xl p-6">
      <h2 className="text-lg font-bold text-text_primary flex items-center gap-2 mb-1"><Palette size={18} className="text-gold" /> Appearance (your personal theme)</h2>
      <p className="text-text_secondary text-sm mb-5">Pick a ready-made theme or set your own colours. Saved to your account.</p>
      <div className="flex gap-3 flex-wrap mb-6">
        {Object.keys(PRESETS).map((p) => (
          <button key={p} data-testid={`preset-${p}`} onClick={() => setPreset(p)} className="px-4 py-2 rounded-xl border border-border_soft text-sm font-semibold text-text_primary hover:border-gold transition-colors">{PRESET_LABELS[p]}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {EDITABLE.map(({ key, label }) => (
          <div key={key}>
            <label className="text-[11px] uppercase tracking-wider text-text_secondary font-semibold block mb-1.5">{label}</label>
            <input type="color" data-testid={`color-${key}`} value={rgbStrToHex(vars[key])} onChange={(e) => setColor(key, e.target.value)} className="w-full h-10 rounded-lg border border-border_soft bg-transparent cursor-pointer" />
          </div>
        ))}
      </div>
      <button data-testid="save-theme-btn" onClick={save} className="bg-primary text-[#111827] font-bold py-3 px-6 rounded-xl flex items-center gap-2 hover:bg-primary_hover transition-colors"><Save size={18} /> Save Theme</button>
    </div>
  );
};


const AgreementEditor = () => {
  const [data, setData] = useState({ title: "", clauses: [] });
  const [msg, setMsg] = useState("");
  const [canEdit, setCanEdit] = useState(false);
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/agreement`).then(r => setData(r.data)).catch(() => {});
    axios.get(`${BACKEND_URL}/api/auth/me`).then(r => setCanEdit(rank(r.data.user.role) >= 70)).catch(() => {});
  }, []);
  const setClause = (i, v) => setData(d => ({ ...d, clauses: d.clauses.map((c, idx) => idx === i ? v : c) }));
  const addClause = () => setData(d => ({ ...d, clauses: [...d.clauses, ""] }));
  const delClause = (i) => setData(d => ({ ...d, clauses: d.clauses.filter((_, idx) => idx !== i) }));
  const save = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/agreement`, { title: data.title, clauses: data.clauses.filter(c => c.trim()) });
      setMsg("Agreement saved!"); setTimeout(() => setMsg(""), 2500);
    } catch (e) { setMsg(e.response?.data?.detail || "Only DM, GM, MD or CEO can edit."); }
  };
  if (!canEdit) return null;
  return (
    <div data-testid="agreement-section" className="bg-card_bg border border-border_soft rounded-xl p-6">
      <h2 className="text-lg font-bold text-text_primary flex items-center gap-2 mb-1"><Receipt size={18} className="text-gold" /> Membership Agreement</h2>
      <p className="text-text_secondary text-sm mb-5">These terms appear on the member signup form. Members must accept them before joining.</p>
      <input value={data.title} onChange={e => setData({ ...data, title: e.target.value })} className="w-full bg-card_bg border border-border_soft rounded-xl p-3 text-text_primary text-sm mb-4 font-semibold" placeholder="Agreement title" />
      <div className="space-y-3">
        {data.clauses.map((c, i) => (
          <div key={i} className="flex gap-2 items-start">
            <span className="text-gold text-sm font-bold mt-3">{i + 1}.</span>
            <textarea data-testid={`clause-${i}`} value={c} onChange={e => setClause(i, e.target.value)} className="flex-1 bg-main_bg border border-border_soft rounded-xl p-3 text-text_primary text-sm" rows={2} />
            <button onClick={() => delClause(i)} className="text-text_secondary hover:text-accent_danger p-2 mt-1"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      <div className="flex gap-3 mt-4">
        <button data-testid="add-clause-btn" onClick={addClause} className="bg-card_bg border border-border_soft text-text_primary px-4 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2 hover:border-gold transition-colors"><Plus size={16} /> Add Clause</button>
        <button data-testid="save-agreement-btn" onClick={save} className="bg-primary text-[#111827] font-bold py-2.5 px-6 rounded-xl flex items-center gap-2 hover:bg-primary_hover transition-colors"><Save size={16} /> Save Agreement</button>
      </div>
      {msg && <p className="text-sm text-accent_success font-bold mt-3">{msg}</p>}
    </div>
  );
};


const Toggle = ({ on, onClick, testid }) => (
  <button type="button" data-testid={testid} onClick={onClick} className={`relative w-11 h-6 rounded-full transition-colors ${on ? "bg-primary" : "bg-border_soft"}`}>
    <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${on ? "translate-x-5" : ""}`} />
  </button>
);

const MemberFieldsEditor = () => {
  const [fields, setFields] = useState([]);
  const [form, setForm] = useState({ label: "", type: "text", required: false });
  const [msg, setMsg] = useState("");
  const [canEdit, setCanEdit] = useState(false);

  const load = useCallback(async () => {
    try { const r = await axios.get(`${BACKEND_URL}/api/member-fields`); setFields(r.data); } catch (e) {}
  }, []);
  useEffect(() => {
    load();
    axios.get(`${BACKEND_URL}/api/auth/me`).then(r => setCanEdit(rank(r.data.user.role) >= 60)).catch(() => {});
  }, [load]);

  const toggleRequired = async (f) => {
    try { await axios.put(`${BACKEND_URL}/api/member-fields/${f.id}`, { required: !f.required }); load(); }
    catch (e) { setMsg(e.response?.data?.detail || "Only Branch Manager and above can edit fields."); setTimeout(() => setMsg(""), 2500); }
  };
  const addField = async (e) => {
    e.preventDefault();
    if (!form.label.trim()) return;
    try { await axios.post(`${BACKEND_URL}/api/member-fields`, form); setForm({ label: "", type: "text", required: false }); load(); }
    catch (e) { setMsg(e.response?.data?.detail || "Only Branch Manager and above can add fields."); setTimeout(() => setMsg(""), 2500); }
  };
  const delField = async (id) => {
    try { await axios.delete(`${BACKEND_URL}/api/member-fields/${id}`); load(); }
    catch (e) { setMsg(e.response?.data?.detail || "Could not delete."); setTimeout(() => setMsg(""), 2500); }
  };

  const builtins = fields.filter(f => f.builtin);
  const customs = fields.filter(f => !f.builtin);

  if (!canEdit) return null;

  const Row = ({ f }) => (
    <div data-testid={`field-row-${f.key}`} className="flex items-center justify-between bg-main_bg border border-border_soft/60 rounded-xl px-4 py-3">
      <div>
        <p className="text-sm text-text_primary font-semibold">{f.label}</p>
        <p className="text-[11px] text-text_secondary">{f.required ? "Mandatory" : "Optional"} · {f.type}{f.builtin ? "" : " · custom"}</p>
      </div>
      <div className="flex items-center gap-3">
        <Toggle on={f.required} onClick={() => toggleRequired(f)} testid={`toggle-required-${f.key}`} />
        {!f.builtin && <button data-testid={`del-field-${f.key}`} onClick={() => delField(f.id)} className="text-text_secondary hover:text-accent_danger"><Trash2 size={15} /></button>}
      </div>
    </div>
  );

  return (
    <div data-testid="member-fields-section" className="bg-card_bg border border-border_soft rounded-xl p-6">
      <h2 className="text-lg font-bold text-text_primary flex items-center gap-2 mb-1"><ListChecks size={18} className="text-gold" /> Member Form Fields</h2>
      <p className="text-text_secondary text-sm mb-5">Toggle whether each field is mandatory on the member signup form, and add your own extra fields.</p>

      <h3 className="text-xs font-bold text-text_secondary uppercase tracking-wider mb-2">Standard Fields</h3>
      <div className="space-y-2 mb-6">{builtins.map(f => <Row key={f.id} f={f} />)}</div>

      <h3 className="text-xs font-bold text-text_secondary uppercase tracking-wider mb-2">Additional Fields</h3>
      <div className="space-y-2 mb-4">
        {customs.length === 0 && <p className="text-text_secondary text-sm">No custom fields yet.</p>}
        {customs.map(f => <Row key={f.id} f={f} />)}
      </div>
      <form onSubmit={addField} className="flex flex-col md:flex-row gap-2 items-stretch md:items-center">
        <input data-testid="new-field-label" value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} placeholder="New field label (e.g. Blood Group)" className="flex-1 bg-main_bg border border-border_soft rounded-xl p-3 text-text_primary text-sm" />
        <select data-testid="new-field-type" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className="bg-main_bg border border-border_soft rounded-xl p-3 text-text_primary text-sm">
          <option value="text">Text</option>
          <option value="number">Number</option>
          <option value="date">Date</option>
        </select>
        <label className="flex items-center gap-2 text-sm text-text_primary px-2 cursor-pointer whitespace-nowrap">
          <input type="checkbox" data-testid="new-field-required" checked={form.required} onChange={e => setForm({ ...form, required: e.target.checked })} className="accent-primary" /> Mandatory
        </label>
        <button data-testid="add-field-btn" type="submit" className="bg-primary text-[#111827] px-5 py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 hover:bg-primary_hover transition-colors"><Plus size={16} /> Add Field</button>
      </form>
      {msg && <p className="text-sm text-accent_danger font-bold mt-3">{msg}</p>}
    </div>
  );
};
